import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, MapPin, Package, Star, MessageCircle, Check } from 'lucide-react'
import Footer from '../layout/Footer'
import { productApi, Product, reviewApi, Review, UserRatingResponse } from '../../services/api'
import { logger } from '../../utils/logger'
import { ReviewItem } from '../ui/ReviewComponents'
import ProductCard from '../product/ProductCard'
import MapPointPicker from '../ui/MapPointPicker'
import { t } from '../../i18n/translations'

interface SellerInfo {
  telegram_id: number
  username?: string
  first_name?: string
  avatar_url?: string
  bio?: string
  home_city_name?: string
  home_country_code?: string
  location_lat?: number | null
  location_lng?: number | null
  location_radius_km?: number | null
  products_count?: number
  member_since?: string
}

export default function SellerProfile() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()

  const [seller, setSeller] = useState<SellerInfo | null>(null)
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Reviews state
  const [sellerReviews, setSellerReviews] = useState<Review[]>([])
  const [sellerRating, setSellerRating] = useState<UserRatingResponse | null>(null)
  const [reviewsError, setReviewsError] = useState(false)

  // Ask a question modal state
  const [showQuestionModal, setShowQuestionModal] = useState(false)
  const [questionMessage, setQuestionMessage] = useState('')
  const [sendingQuestion, setSendingQuestion] = useState(false)
  const [questionSent, setQuestionSent] = useState(false)
  const [questionError, setQuestionError] = useState(false)

  useEffect(() => {
    const fetchSellerData = async () => {
      if (!userId) return

      try {
        setLoading(true)
        const storeData = await productApi.getPersonalStore(userId)

        if (storeData) {
          setSeller({
            telegram_id: storeData.user?.telegram_id,
            username: storeData.user?.username,
            first_name: storeData.user?.first_name,
            home_city_name: storeData.user?.home_city_name || undefined,
            home_country_code: storeData.user?.home_country_code || undefined,
            location_lat: storeData.user?.location_lat,
            location_lng: storeData.user?.location_lng,
            location_radius_km: storeData.user?.location_radius_km,
            products_count: storeData.products?.length || 0,
          })
          setProducts(storeData.products || [])

          // Fetch seller rating and reviews
          if (storeData.user?.telegram_id) {
            try {
              const tgId = storeData.user.telegram_id.toString()
              const [reviewsData, ratingsData] = await Promise.all([
                reviewApi.getUserReviews(tgId, { type: 'seller', limit: 10 }),
                reviewApi.getUserRatings(tgId),
              ])
              setSellerReviews(reviewsData.reviews || [])
              setSellerRating(ratingsData)
              setReviewsError(false)
            } catch {
              // Reviews are non-blocking, but surface a load error in the list.
              setReviewsError(true)
            }
          }
        }
      } catch (err) {
        logger.error('Failed to fetch seller profile', err)
        setError('Failed to load seller profile')
      } finally {
        setLoading(false)
      }
    }

    fetchSellerData()
  }, [userId])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    )
  }

  if (error || !seller) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <p className="text-gray-500 mb-4">{error || 'Seller not found'}</p>
        <button
          onClick={() => navigate(-1)}
          className="text-blue-500 hover:text-blue-600"
        >
          {t('common.back')}
        </button>
      </div>
    )
  }

  const displayName = seller.first_name || seller.username || 'Seller'

  // Hide "ask a question" when viewing your own profile.
  const currentUserId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id
  const isSelf = currentUserId != null && currentUserId === seller.telegram_id

  const closeQuestionModal = () => {
    if (sendingQuestion) return
    setShowQuestionModal(false)
    setQuestionMessage('')
    setQuestionError(false)
    setQuestionSent(false)
  }

  const handleSendQuestion = async () => {
    const message = questionMessage.trim()
    if (!message || sendingQuestion) return
    setSendingQuestion(true)
    setQuestionError(false)
    try {
      await productApi.contactUser(seller.telegram_id, message)
      setQuestionSent(true)
      setTimeout(() => {
        setShowQuestionModal(false)
        setQuestionMessage('')
        setQuestionSent(false)
      }, 1500)
    } catch (err) {
      logger.error('Failed to send seller question', err)
      setQuestionError(true)
    } finally {
      setSendingQuestion(false)
    }
  }

  const hasLocation = seller.location_lat != null && seller.location_lng != null
  const locationRadius = seller.location_radius_km ?? 5

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-medium">{t('common.back')}</span>
          </button>
          <h1 className="text-lg font-semibold text-gray-800">
            {t('profile.seller')}
          </h1>
          <div className="w-16" />
        </div>
      </div>

      {/* Seller Info Card */}
      <div className="p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-2xl font-bold">
              {displayName.charAt(0).toUpperCase()}
            </div>

            {/* Info */}
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-800">{displayName}</h2>
              {seller.username && (
                <p className="text-gray-500">@{seller.username}</p>
              )}
              {seller.home_city_name && (
                <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
                  <MapPin className="w-4 h-4" />
                  <span>{seller.home_city_name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Ask a question */}
          {!isSelf && (
            <button
              onClick={() => setShowQuestionModal(true)}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-blue-500 text-white
                py-3 rounded-xl font-semibold text-sm hover:bg-blue-600 transition-colors
                active:scale-[0.98] shadow-[0_4px_12px_rgba(59,130,246,0.3)]"
            >
              <MessageCircle className="w-4 h-4" />
              <span>💬 {t('seller.askQuestion')}</span>
            </button>
          )}

          {/* Stats */}
          <div className="flex gap-6 mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />
              <span className="text-gray-800 font-medium">{products.length}</span>
              <span className="text-gray-500">{t('profile.myProducts')}</span>
            </div>
            {sellerRating && (
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="text-gray-800 font-medium">
                  {sellerRating.avg_seller_rating > 0 ? sellerRating.avg_seller_rating.toFixed(1) : '—'}
                </span>
                <span className="text-gray-500">({sellerRating.seller_review_count})</span>
              </div>
            )}
          </div>

          {/* Approximate location */}
          {hasLocation && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                📍 {t('location.approxArea')}
              </h4>
              <p className="text-sm text-gray-500 mb-3">
                {seller.home_city_name && (
                  <span>
                    {seller.home_city_name}
                    {seller.home_country_code ? `, ${seller.home_country_code}` : ''}
                    {' · '}
                  </span>
                )}
                {t('location.withinRadius').replace('{radius}', String(locationRadius))}
              </p>
              <div className="pointer-events-none">
                <MapPointPicker
                  lat={seller.location_lat}
                  lng={seller.location_lng}
                  radiusKm={locationRadius}
                  onChange={() => {}}
                />
              </div>
            </div>
          )}

          {/* Bio */}
          {seller.bio && (
            <p className="mt-4 text-gray-600">{seller.bio}</p>
          )}

          {/* Seller Reviews */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">
              {t('reviews.title')} ({sellerRating?.seller_review_count ?? sellerReviews.length})
            </h4>
            {reviewsError ? (
              <p className="text-sm text-gray-500">{t('reviews.loadError')}</p>
            ) : sellerReviews.length === 0 ? (
              <p className="text-sm text-gray-500">{t('reviews.noReviews')}</p>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                {sellerReviews.map((r) => (
                  <ReviewItem key={r.id} review={r} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="px-4 pb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">
          {t('profile.sellerProducts')}
        </h3>

        {products.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            {t('common.noResults')}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
              />
            ))}
          </div>
        )}
        <Footer />
      </div>

      {/* Ask a Question Modal */}
      {showQuestionModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-4 z-[60]"
          onClick={(e) => { if (e.target === e.currentTarget) closeQuestionModal() }}
        >
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            {questionSent ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-green-600 font-semibold">{t('seller.questionSent')}</p>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold text-gray-800 mb-1">{t('seller.askQuestion')}</h2>
                <p className="text-sm text-gray-500 mb-4">{t('seller.questionHint')}</p>

                <textarea
                  value={questionMessage}
                  onChange={(e) => setQuestionMessage(e.target.value)}
                  maxLength={500}
                  rows={4}
                  placeholder={t('seller.questionPlaceholder')}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800
                    focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm resize-none"
                />

                {questionError && (
                  <p className="mt-3 text-sm text-red-600">{t('seller.questionError')}</p>
                )}

                <div className="flex gap-3 mt-4">
                  <button
                    onClick={closeQuestionModal}
                    disabled={sendingQuestion}
                    className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm
                      hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleSendQuestion}
                    disabled={sendingQuestion || questionMessage.trim().length === 0}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-500 text-white py-3 rounded-xl
                      font-semibold text-sm hover:bg-blue-600 transition-colors
                      disabled:opacity-50 shadow-[0_4px_12px_rgba(59,130,246,0.3)]"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>{t('seller.askQuestion')}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
